import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { ExecutionListItem, ExecutionSortField, ExecutionTemplateSummary, SortDirection } from '../../../services/executionService';
import {
  formatDurationClockFromMinutes,
  getExecutionRemainingSlaMinutes,
  getOperationalRiskLevel,
  getOperationalRiskPresentation,
} from '../../shared/utils/operationalRisk';
import { ExecutionStatusBadge } from './ExecutionStatusBadge';

interface ExecutionGridProps {
  items: ExecutionListItem[];
  total: number;
  page: number;
  pageSize: number;
  sortField: ExecutionSortField;
  sortDirection: SortDirection;
  activeSortLabel?: string;
  onSortChange: (field: ExecutionSortField, direction: SortDirection) => void;
  onPageChange: (page: number) => void;
  onRowSelect: (id: string) => void;
  onRowOpen?: (id: string) => void;
  selectedId?: string | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry: () => void;
  onEmptyCta: () => void;
  canCreateExecution: boolean;
  templates?: ExecutionTemplateSummary[];
}

const COLUMNS: Array<{ key: string; label: string; sortable?: boolean; sortKey?: ExecutionSortField }> = [
  { key: 'execution', label: 'CODIGO' },
  { key: 'template', label: 'TEMPLATE' },
  { key: 'stage', label: 'ETAPA ATUAL' },
  { key: 'status', label: 'STATUS', sortable: true, sortKey: 'status' },
  { key: 'sla', label: 'SLA RESTANTE', sortable: true, sortKey: 'sla' },
  { key: 'elapsed', label: 'TEMPO DECORRIDO', sortable: true, sortKey: 'elapsed' },
  { key: 'risk', label: 'RISCO' },
  { key: 'priority', label: 'PRIORIDADE', sortable: true, sortKey: 'priority' },
];

function formatRemainingSla(item: ExecutionListItem) {
  return formatDurationClockFromMinutes(getExecutionRemainingSlaMinutes(item));
}

function formatTemplateLabel(value?: string | null) {
  if (!value) return '-';
  return value.replace(/\s*\(v[^)]+\)$/i, '').trim();
}

function getActiveSortLabel(sortField: ExecutionSortField) {
  switch (sortField) {
    case 'sla':
      return 'SLA';
    case 'elapsed':
      return 'TEMPO';
    case 'priority':
      return 'PRIORIDADE';
    case 'status':
      return 'STATUS';
    case 'started_at':
      return 'INICIO';
    default:
      return sortField.toUpperCase();
  }
}

export function ExecutionGrid({
  items,
  total,
  page,
  pageSize,
  sortField,
  sortDirection,
  activeSortLabel,
  onSortChange,
  onPageChange,
  onRowSelect,
  onRowOpen,
  selectedId,
  isLoading,
  isFetching,
  error,
  onRetry,
  onEmptyCta,
  canCreateExecution,
  templates = [],
}: ExecutionGridProps) {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  if (error) {
    return (
      <div className="enterprise-card-surface flex min-h-[280px] flex-col items-center justify-center gap-3 px-6 text-center enterprise-text-secondary">
        <AlertCircle size={24} className="risk-critical" />
        <div>
          <p className="text-sm font-semibold enterprise-text-primary">Falha ao carregar instancias</p>
          <p className="mt-1 text-xs enterprise-text-secondary">{error.message}</p>
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="enterprise-button-secondary rounded-[4px] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em]"
        >
          RECARREGAR
        </button>
      </div>
    );
  }

  if (!isLoading && items.length === 0) {
    const hasTemplates = templates.length > 0;

    return (
      <div className="enterprise-card-surface flex min-h-[300px] flex-col justify-center gap-4 border-dashed px-6 py-8 enterprise-text-secondary">
        <div className="space-y-1">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] enterprise-text-muted">Nenhuma instância</p>
          <p className="text-sm font-semibold enterprise-text-primary">Nenhuma execução encontrada</p>
          <p className="max-w-md text-xs enterprise-text-secondary">
            {canCreateExecution
              ? 'Publique um modelo corporativo e inicie uma nova execução para preencher o painel técnico.'
              : 'Revise filtros e criterios operacionais para localizar execucoes ativas.'}
          </p>
        </div>

        {canCreateExecution && hasTemplates ? (
          <button
            type="button"
            onClick={onEmptyCta}
            className="enterprise-button-secondary inline-flex w-fit items-center rounded-[4px] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em]"
          >
            Nova Execução
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-h-[360px] bg-transparent">
      <div className="enterprise-table-shell overflow-x-auto">
        <table className="min-w-full table-fixed text-[11px]">
          <thead className="enterprise-table-header text-left font-mono text-[10px] uppercase tracking-[0.14em]">
            <tr>
              {COLUMNS.map((column) => {
                const sortKey = column.sortKey;
                const isSorted = Boolean(sortKey && sortField === sortKey);
                const nextDirection: SortDirection = isSorted && sortDirection === 'asc' ? 'desc' : 'asc';
                return (
                  <th key={column.key} className="px-3 py-2 font-medium">
                    {column.sortable && sortKey ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(sortKey, nextDirection)}
                        className={`flex items-center gap-1 ${isSorted ? 'enterprise-text-primary' : ''}`}
                      >
                        {column.label}
                        {isSorted && (sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="enterprise-table-row animate-pulse">
                    <td colSpan={COLUMNS.length} className="px-3 py-2">
                      <div className="h-3 rounded-[2px] enterprise-surface-3" />
                    </td>
                  </tr>
                ))
              : items.map((item) => {
                  const isSelected = selectedId === item.id;
                  const templateLabel = formatTemplateLabel(item.templateName);
                  const currentStageLabel = item.currentStageName?.replace(/\s+/g, '_').toUpperCase() || '-';
                  const remainingSla = formatRemainingSla(item);
                  const remainingSlaDisplay = remainingSla === 'N/D' ? '—' : remainingSla;
                  const remainingMinutes = getExecutionRemainingSlaMinutes(item);
                  const risk = getOperationalRiskPresentation(getOperationalRiskLevel(remainingMinutes, null));
                  const isRiskCritical = getOperationalRiskLevel(remainingMinutes, null) === 'CRITICO';
                  const isRiskAlert = getOperationalRiskLevel(remainingMinutes, null) === 'ALERTA';

                  return (
                    <tr
                      key={item.id}
                      onClick={() => onRowSelect(item.id)}
                      onDoubleClick={() => onRowOpen?.(item.id)}
                      className={`enterprise-table-row cursor-pointer ${isSelected ? 'is-selected' : ''} ${
                        isRiskCritical ? 'bg-red-50/60 dark:bg-red-950/20' :
                        isRiskAlert ? 'bg-amber-50/40 dark:bg-amber-950/15' : ''
                      }`}
                    >
                      <td
                        className={`relative px-3 py-2 align-middle font-semibold enterprise-text-primary ${
                          isSelected
                            ? 'pl-4 before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[#718198] before:shadow-[0_0_6px_rgba(113,129,152,0.28)]'
                            : ''
                        }`}
                      >
                        {item.referenceCode || 'Sem codigo'}
                      </td>
                      <td className="truncate px-3 py-2 align-middle enterprise-text-secondary">
                        {templateLabel}
                      </td>
                      <td className="truncate px-3 py-2 align-middle font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-primary">
                        {currentStageLabel}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <ExecutionStatusBadge status={item.status} />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span
                          className={`font-mono font-semibold ${
                            remainingSla === 'N/D'
                              ? 'enterprise-text-muted'
                              : remainingSla.startsWith('-')
                                ? 'text-red-700 dark:text-red-300'
                                : remainingMinutes !== null && remainingMinutes <= 60
                                  ? 'text-amber-700 dark:text-amber-300'
                                  : 'enterprise-text-primary'
                          }`}
                        >
                          {remainingSlaDisplay}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle font-mono enterprise-tech-value">
                        {formatDurationClockFromMinutes(item.elapsedMinutes)}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${risk.className}`}>
                          {risk.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle font-mono text-[10px] uppercase tracking-[0.12em] enterprise-text-primary">
                        {item.priority || '-'}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-enterprise-border-strong px-3 py-2 font-mono text-[11px] uppercase tracking-[0.12em] enterprise-text-secondary">
        <span>PAGINA {page} / {totalPages}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page === 1}
            className="enterprise-button-secondary rounded-[4px] px-2 py-1 disabled:opacity-50"
          >
            ANTERIOR
          </button>
          <button
            type="button"
            onClick={() => onPageChange(Math.min(page + 1, totalPages))}
            disabled={page === totalPages}
            className="enterprise-button-secondary rounded-[4px] px-2 py-1 disabled:opacity-50"
          >
            PROXIMA
          </button>
        </div>
      </div>
    </div>
  );
}
